import { computed, reactive, ref } from 'vue';
import { fetchConfiguratorRecommendations, fetchConfiguratorSchema } from './api';

const FEEDBACK_DELTA = {
    like: 0.5,
    dislike: -0.3,
};

export function useAiConfigurator() {
    const schema = ref(null);
    const isLoadingSchema = ref(false);
    const isLoadingResults = ref(false);
    const isRefreshingResults = ref(false);
    const errorMessage = ref('');
    const infoMessage = ref('');
    const showResults = ref(false);
    const stepIndex = ref(0);
    const sortMode = ref('match_desc');
    const answers = reactive({});
    const results = ref([]);
    const meta = ref({});
    const userProfile = ref({});
    const tagAdjustments = reactive({});
    const feedbackByProduct = reactive({});
    const extraTags = ref([]);

    const steps = computed(() => {
        if (!schema.value || !Array.isArray(schema.value.steps)) {
            return [];
        }

        return schema.value.steps;
    });

    const requestDefaults = computed(() => {
        const defaults = schema.value?.defaults || {};
        const topN = Number(defaults.top_n);
        const minScore = Number(defaults.min_score);

        return {
            top_n: Number.isFinite(topN) && topN > 0 ? topN : 12,
            min_score: Number.isFinite(minScore) && minScore > 0 ? minScore : 25,
        };
    });

    const currentStep = computed(() => steps.value[stepIndex.value] || null);

    const progress = computed(() => {
        const total = steps.value.length;
        if (!total) {
            return 0;
        }

        return Math.round(((stepIndex.value + 1) / total) * 100);
    });

    const canGoBack = computed(() => stepIndex.value > 0);

    const canGoForward = computed(() => {
        if (!currentStep.value) {
            return false;
        }

        if (!currentStep.value.required) {
            return true;
        }

        const selected = answers[currentStep.value.id];
        if (currentStep.value.type === 'multi') {
            return Array.isArray(selected) && selected.length > 0;
        }

        return typeof selected === 'string' && selected.length > 0;
    });

    const sortedResults = computed(() => {
        const products = Array.isArray(results.value) ? [...results.value] : [];

        switch (sortMode.value) {
            case 'price_asc':
                return products.sort((a, b) => Number(a.price) - Number(b.price));
            case 'price_desc':
                return products.sort((a, b) => Number(b.price) - Number(a.price));
            case 'match_desc':
            default:
                return products.sort((a, b) => Number(b.match_score) - Number(a.match_score));
        }
    });

    async function init() {
        isLoadingSchema.value = true;
        errorMessage.value = '';

        try {
            const fetchedSchema = await fetchConfiguratorSchema();
            if (!fetchedSchema || !Array.isArray(fetchedSchema.steps)) {
                throw new Error('Brak poprawnego schematu konfiguratora.');
            }

            schema.value = fetchedSchema;
        } catch (error) {
            errorMessage.value = error?.message || 'Nie udało się załadować konfiguratora.';
        } finally {
            isLoadingSchema.value = false;
        }
    }

    function isCurrentStepOptional() {
        return !!currentStep.value && !currentStep.value.required;
    }

    function isOptionSelected(step, optionId) {
        const selected = answers[step.id];
        if (step.type === 'multi') {
            return Array.isArray(selected) && selected.includes(optionId);
        }

        return selected === optionId;
    }

    function onOptionClick(step, option) {
        if (!step || !option) {
            return;
        }

        if (step.type === 'multi') {
            toggleMultiOption(step, option);
            return;
        }

        answers[step.id] = option.id;
    }

    function toggleMultiOption(step, option) {
        const current = Array.isArray(answers[step.id]) ? [...answers[step.id]] : [];
        const neutralOptionIds = (step.options || []).filter((item) => item?.neutral).map((item) => item.id);

        if (option.neutral) {
            answers[step.id] = [option.id];
            return;
        }

        const withoutNeutral = current.filter((id) => !neutralOptionIds.includes(id));
        if (withoutNeutral.includes(option.id)) {
            const updated = withoutNeutral.filter((id) => id !== option.id);
            if (updated.length) {
                answers[step.id] = updated;
            } else {
                delete answers[step.id];
            }
            return;
        }

        answers[step.id] = [...withoutNeutral, option.id];
    }

    function goToPreviousStep() {
        if (!canGoBack.value) {
            return;
        }

        stepIndex.value -= 1;
    }

    async function goToNextStep() {
        if (!canGoForward.value) {
            return;
        }

        const lastStepIndex = steps.value.length - 1;
        if (stepIndex.value >= lastStepIndex) {
            await runRecommendations();
            return;
        }

        stepIndex.value += 1;
    }

    async function skipStep() {
        if (!currentStep.value || currentStep.value.required) {
            return;
        }

        delete answers[currentStep.value.id];
        await goToNextStep();
    }

    function formatPrice(value) {
        const price = Number(value || 0);
        return `${price.toFixed(2)} zł`;
    }

    function getPayload() {
        const plainAnswers = JSON.parse(JSON.stringify(answers || {}));
        return {
            answers: plainAnswers,
            top_n: requestDefaults.value.top_n,
            min_score: requestDefaults.value.min_score,
            tag_adjustments: { ...tagAdjustments },
            extra_tags: [...extraTags.value],
        };
    }

    async function runRecommendations(options = {}) {
        const keepVisible = Boolean(
            options?.background &&
            showResults.value &&
            Array.isArray(results.value) &&
            results.value.length > 0
        );

        if (keepVisible) {
            isRefreshingResults.value = true;
        } else {
            isLoadingResults.value = true;
        }

        errorMessage.value = '';
        if (!options?.preserveInfoMessage) {
            infoMessage.value = '';
        }

        try {
            const recommendationData = await fetchConfiguratorRecommendations(getPayload());
            const fetchedResults = Array.isArray(recommendationData?.results)
                ? recommendationData.results
                : [];

            results.value = fetchedResults;
            meta.value = recommendationData?.meta || {};
            userProfile.value = recommendationData?.user_profile || {};
            showResults.value = true;

            if (meta.value?.used_fallback) {
                infoMessage.value =
                    'To najbliższe dopasowania. Jeśli chcesz, zawęź kryteria i sprawdź ponownie.';
            }
        } catch (error) {
            errorMessage.value = error?.message || 'Nie udało się pobrać rekomendacji.';
        } finally {
            if (keepVisible) {
                isRefreshingResults.value = false;
            } else {
                isLoadingResults.value = false;
            }
        }
    }

    function getFeedbackType(productId) {
        return feedbackByProduct[productId] || null;
    }

    async function applyFeedback(product, type) {
        if (!product || !Array.isArray(product.tags) || !FEEDBACK_DELTA[type]) {
            return;
        }

        const productId = Number(product.id);
        const previousType = getFeedbackType(productId);
        if (previousType === type) {
            updateTagAdjustments(product.tags, -FEEDBACK_DELTA[type]);
            delete feedbackByProduct[productId];
            await runRecommendations({ background: true, preserveInfoMessage: true });
            return;
        }

        if (previousType && FEEDBACK_DELTA[previousType]) {
            updateTagAdjustments(product.tags, -FEEDBACK_DELTA[previousType]);
        }

        feedbackByProduct[productId] = type;
        updateTagAdjustments(product.tags, FEEDBACK_DELTA[type]);
        await runRecommendations({ background: true, preserveInfoMessage: true });
    }

    function updateTagAdjustments(tags, delta) {
        if (!Array.isArray(tags) || !delta) {
            return;
        }

        tags.forEach((tag) => {
            const normalizedTag = String(tag || '').trim();
            if (!normalizedTag) {
                return;
            }

            const nextValue = Number(tagAdjustments[normalizedTag] || 0) + Number(delta);
            if (Math.abs(nextValue) < 0.001) {
                delete tagAdjustments[normalizedTag];
                return;
            }

            tagAdjustments[normalizedTag] = Number(nextValue.toFixed(3));
        });
    }

    async function showSimilar(product) {
        if (!product || !Array.isArray(product.tags) || !product.tags.length) {
            return;
        }

        const nextExtraTags = new Set(extraTags.value);
        product.tags.slice(0, 12).forEach((tag) => nextExtraTags.add(String(tag)));
        extraTags.value = Array.from(nextExtraTags);

        infoMessage.value = 'Dodaliśmy podobne cechy do wyszukiwania i przeliczamy ranking.';
        await runRecommendations({ background: true, preserveInfoMessage: true });
    }

    function editAnswers() {
        showResults.value = false;
        stepIndex.value = 0;
    }

    function resetAll() {
        Object.keys(answers).forEach((key) => delete answers[key]);
        Object.keys(tagAdjustments).forEach((key) => delete tagAdjustments[key]);
        Object.keys(feedbackByProduct).forEach((key) => delete feedbackByProduct[key]);
        extraTags.value = [];
        results.value = [];
        meta.value = {};
        userProfile.value = {};
        infoMessage.value = '';
        errorMessage.value = '';
        showResults.value = false;
        stepIndex.value = 0;
        sortMode.value = 'match_desc';
    }

    return {
        answers,
        canGoBack,
        canGoForward,
        currentStep,
        editAnswers,
        errorMessage,
        formatPrice,
        getFeedbackType,
        goToNextStep,
        goToPreviousStep,
        infoMessage,
        init,
        isCurrentStepOptional,
        isLoadingResults,
        isRefreshingResults,
        isLoadingSchema,
        isOptionSelected,
        meta,
        onOptionClick,
        progress,
        resetAll,
        runRecommendations,
        showResults,
        showSimilar,
        skipStep,
        sortMode,
        sortedResults,
        stepIndex,
        steps,
        applyFeedback,
    };
}
